/** 余料导入模板表头（列顺序即为样例行字段顺序） */
export const exportTableHeader = [
  '余料名称',
  '板材材质',
  '板材颜色',
  '板材形状',
  '厚度',
  '长度',
  '短边长度',
  '宽度',
  '短边宽度',
  '高光板',
  '修边',
] as const

export type SurplusTemplateHeader = (typeof exportTableHeader)[number]

/** 跟随原片标准板修边（导出/导入展示值） */
export const SURPLUS_TRIM_FOLLOW = '/'

/**
 * 修边展示：上{上}-下{下}-左{左}-右{右}
 * 例：上0-下5-左0-右5
 */
const SURPLUS_TRIM_INTEGER_DIGITS = 5
const SURPLUS_TRIM_DECIMAL_DIGITS = 2
const SURPLUS_TRIM_VALUE_PATTERN = `\\d{1,${SURPLUS_TRIM_INTEGER_DIGITS}}(?:\\.\\d{1,${SURPLUS_TRIM_DECIMAL_DIGITS}})?`
const SURPLUS_TRIM_VALUE_REG = new RegExp(`^${SURPLUS_TRIM_VALUE_PATTERN}$`)
const TRIM_LABEL_REG = new RegExp(
  `^上(${SURPLUS_TRIM_VALUE_PATTERN})-下(${SURPLUS_TRIM_VALUE_PATTERN})-左(${SURPLUS_TRIM_VALUE_PATTERN})-右(${SURPLUS_TRIM_VALUE_PATTERN})$`
)

/** 修边输入值：非负数，最多 5 位整数、2 位小数 */
export function isValidSurplusTrimValue(value: unknown): boolean {
  return SURPLUS_TRIM_VALUE_REG.test(String(value ?? '').trim())
}

/** 按修边数值规则清洗输入框内容 */
export function sanitizeSurplusTrimValueInput(value: unknown): string {
  const text = String(value ?? '')
  const numericPrefix = text.match(/^\d*(?:\.\d*)?/)?.[0] || ''
  const [integerPart, decimalPart] = numericPrefix.split('.')
  const integer = integerPart.slice(0, SURPLUS_TRIM_INTEGER_DIGITS)
  if (!numericPrefix.includes('.')) return integer
  return `${integer}.${(decimalPart || '').slice(
    0,
    SURPLUS_TRIM_DECIMAL_DIGITS
  )}`
}

/**
 * 是否为合法修边单元格：空、/、上x-下x-左x-右x
 * （空在导出时会写成 /）
 */
export function isValidSurplusTrimDisplay(
  value: unknown,
  shape?: unknown
): boolean {
  const text = String(value ?? '').trim()
  if (!text || text === SURPLUS_TRIM_FOLLOW) return true
  if (shape !== undefined && shape !== '矩形' && shape !== 0 && shape !== '0') {
    return false
  }
  return TRIM_LABEL_REG.test(text)
}

/**
 * 表格展示/导出用
 * - 跟随或无值 → /
 * - 四边 → 上a-下b-左c-右d
 * - 单值四边相等也走标签格式
 */
export function formatSurplusTrimDisplay(options: {
  surplusTrimEdge?: string | number | null
  trimmingMethod?: string
  isCustomTrim?: boolean
  topTrim?: string | number
  bottomTrim?: string | number
  leftTrim?: string | number
  rightTrim?: string | number
  trimValue?: string | number
  shape?: number | string
}): string {
  const {
    surplusTrimEdge,
    trimmingMethod,
    isCustomTrim,
    topTrim,
    bottomTrim,
    leftTrim,
    rightTrim,
    trimValue,
    shape,
  } = options

  // L 形 / 异形：与列表展示一致
  if (shape !== undefined && shape !== 0 && shape !== '0') {
    return SURPLUS_TRIM_FOLLOW
  }
  if (trimmingMethod === 'follow') {
    return SURPLUS_TRIM_FOLLOW
  }

  if (surplusTrimEdge != null && String(surplusTrimEdge).trim() !== '') {
    return formatTrimEdgeRawToDisplay(String(surplusTrimEdge).trim())
  }

  if (isCustomTrim) {
    const edges = [topTrim, bottomTrim, leftTrim, rightTrim]
    if (edges.every((v) => v !== '' && v != null)) {
      return `上${topTrim}-下${bottomTrim}-左${leftTrim}-右${rightTrim}`
    }
    return SURPLUS_TRIM_FOLLOW
  }

  const single = String(trimValue ?? '').trim()
  if (single) {
    return `上${single}-下${single}-左${single}-右${single}`
  }
  return SURPLUS_TRIM_FOLLOW
}

/** 接口存的 5 或 0,5,0,5 → 展示标签 */
function formatTrimEdgeRawToDisplay(raw: string): string {
  if (!raw || raw === SURPLUS_TRIM_FOLLOW) return SURPLUS_TRIM_FOLLOW
  if (TRIM_LABEL_REG.test(raw)) return raw
  if (raw.includes(',')) {
    const parts = raw.split(',').map((p) => p.trim())
    if (parts.length === 4) {
      return `上${parts[0]}-下${parts[1]}-左${parts[2]}-右${parts[3]}`
    }
  }
  // 单值
  return `上${raw}-下${raw}-左${raw}-右${raw}`
}

/**
 * 导入单元格 → 接口字段
 * 空、/ → follow + 空 surplus_trim_edge
 * 上0-下5-左0-右5 → custom + "0,5,0,5"
 */
export function parseSurplusTrimImport(
  value: unknown,
  shape?: unknown
): {
  surplus_trim_edge: string
  trimming_method: 'follow' | 'custom'
} {
  const text = String(value ?? '').trim()
  if (
    !text ||
    text === SURPLUS_TRIM_FOLLOW ||
    (shape !== undefined && shape !== '矩形' && shape !== 0 && shape !== '0')
  ) {
    return {
      surplus_trim_edge: '',
      trimming_method: 'follow',
    }
  }

  const match = text.match(TRIM_LABEL_REG)
  if (match) {
    const [, top, bottom, left, right] = match
    return {
      surplus_trim_edge: `${top},${bottom},${left},${right}`,
      trimming_method: 'custom',
    }
  }

  return {
    surplus_trim_edge: '',
    trimming_method: 'follow',
  }
}

/**
 * 导入模板样例行（按 exportTableHeader 列顺序一一对应）
 * 预留位置：直接改/增/删本数组即可，无需改下载逻辑
 *
 * 填写说明：
 * - 板材形状：矩形 | L形
 * - 矩形时：短边长度、短边宽度 留空
 * - L 形时：需填 短边长度、短边宽度
 * - 高光板：高光板 | 非高光板
 * - 修边：/（跟随标准板，空也视为 /）或 上0-下5-左0-右5
 */
export const exportTableSampleRows: string[][] = [
  // -------- 样例1：矩形 + 跟随修边（预留，可按业务改值） --------
  [
    '样例一', // 余料名称
    '多层实木', // 板材材质
    'T01', // 板材颜色
    '矩形', // 板材形状
    '18', // 厚度
    '1000', // 长度
    '', // 短边长度
    '500', // 宽度
    '', // 短边宽度
    '非高光板', // 高光板
    '/', // 修边
  ],
  // -------- 样例2：L 形 + 跟随修边（预留） --------
  [
    '样例二',
    '颗粒板',
    'T01',
    'L形',
    '18',
    '1000',
    '500',
    '500',
    '300',
    '非高光板',
    '/',
  ],
  // -------- 样例3：矩形 + 四边修边（预留） --------
  [
    '样例三',
    '焊接板',
    '银色',
    '矩形',
    '18',
    '1200',
    '',
    '600',
    '',
    '非高光板',
    '上0-下5-左0-右5',
  ],
]

/** 下载用：表头 + 样例行（样例空修边统一写成 /） */
export function getSurplusImportTemplateData(): string[][] {
  const header = [...exportTableHeader]
  const trimColIndex = header.indexOf('修边')
  const rows = exportTableSampleRows.map((row) => {
    const next = [...row]
    if (trimColIndex >= 0) {
      const trimText = String(next[trimColIndex] ?? '').trim()
      next[trimColIndex] = trimText || SURPLUS_TRIM_FOLLOW
    }
    return next
  })
  return [header, ...rows]
}
